// src/sectors/pilates/attendance/ui/AttendanceMarkModal/AttendanceMarkModal.jsx
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { ParticipantAttendanceRow } from './components/ParticipantAttendanceRow';
import { AttendanceDecisionModal } from './components/AttendanceDecisionModal';
import { useAttendanceMarkFlow } from './utils/useAttendanceMarkFlow';
import { S } from './utils/attendanceConstants';
import { errText, minToHm } from './utils/attendanceUtils';
import { RenewPackageModal } from './modals/RenewPackageModal';
import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';
import { attendanceApi } from '../../api/attendanceApi';
import { uiBus } from 'shared/api/uiBus';

export function AttendanceMarkModal({
  open,
  onClose,
  session,
  trainersById,
  fetchParticipants,
  onSave: onSaveProp,
  onWeeklyRefresh, // ✅ EKLE
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const trName = useMemo(() => {
    const tr = trainersById?.get(String(session?.trainerId));
    return tr?.name || session?.trainerName || '—';
  }, [trainersById, session?.trainerId, session?.trainerName]);

  const {
    loading,
    people,
    byPerson,
    setByPerson,
    invalidCount,
    commitSingle,
    saveLoading,
    decision,
    submitError,
    decisionModalOpen,
    setDecisionModalOpen,
    pendingCommit,
    validateAndMaybeCommit,
    confirmDecisionAndCommit,
  } = useAttendanceMarkFlow({
    open,
    sessionId: session?.id,
    fetchParticipants,
    peopleFetchLimit: 5,
  });

  // ✅ Seansın yoklaması daha önce girilmiş mi?
  const hasAnyMarked = useMemo(() => {
    return (people || []).some((p) => {
      const st = String(p?.current?.status || 'PENDING');
      return st && st !== 'PENDING';
    });
  }, [people]);

  // ✅ Page mode: yoklama girilmişse default VIEW (detay ekranı)
  const [pageMode, setPageMode] = useState('EDIT');

  useEffect(() => {
    if (!open) return;
    setPageMode(hasAnyMarked ? 'VIEW' : 'EDIT');
  }, [open, hasAnyMarked]);

  const [expandedPid, setExpandedPid] = useState(null);
  const [expandedMode, setExpandedMode] = useState('DETAIL'); // DETAIL | EDIT

  const openDetail = (pid) => {
    setExpandedPid(pid);
    setExpandedMode('DETAIL');
  };
  const openEdit = (pid) => {
    setExpandedPid(pid);
    setExpandedMode('EDIT');
  };
  const closeAccordion = () => {
    setExpandedPid(null);
    setExpandedMode('DETAIL');
  };
  // AttendanceMarkModal.jsx içinde
  const [submitWarn, setSubmitWarn] = useState(null); // {message} | null
  const [submitBlock, setSubmitBlock] = useState(null); // {message} | null
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (!submitError) return;
    toast('error', errText(submitError) || 'Yoklama kaydedilemedi.');
  }, [open, submitError]);
  useEffect(() => {
    if (!open) return;
    if (!submitBlock?.message) return;
    toast('error', submitBlock.message);
  }, [open, submitBlock]);
  useEffect(() => {
    if (!open) return;
    if (!submitWarn?.message) return;
    toast('warning', submitWarn.message);
  }, [open, submitWarn]);
  // accordion state (UI-only)
  // ✅ initial snapshot (change detection)
  const initialByPid = useMemo(() => {
    const map = {};
    (people || []).forEach((p) => {
      const pid = String(p?.sessionParticipantId ?? p?.id);
      map[pid] = {
        status: p?.current?.status ?? 'PENDING',
        note: p?.current?.note ?? '',
        selectedMemberPlanId: null,
        limitDecision: null,
        packageExceedPolicy: null,
      };
    });
    return map;
  }, [people]);

  const hasAnyChange = useMemo(() => {
    const keys = Object.keys(initialByPid || {});
    if (!keys.length) return false;

    for (const pid of keys) {
      const init = initialByPid[pid] || { status: 'PENDING', note: '' };
      const cur = byPerson?.[pid] || init;

      if (String(init.status) !== String(cur.status)) return true;
      if (String(init.note || '').trim() !== String(cur.note || '').trim()) return true;

      if (String(init.selectedMemberPlanId || '') !== String(cur.selectedMemberPlanId || ''))
        return true;
      if (String(init.limitDecision || '') !== String(cur.limitDecision || '')) return true;
      if (String(init.packageExceedPolicy || '') !== String(cur.packageExceedPolicy || ''))
        return true;
    }
    return false;
  }, [initialByPid, byPerson]);

  // Renew modal state
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewParticipant, setRenewParticipant] = useState(null);
  const handleSaved = async (payload) => {
    await Promise.resolve(onSaveProp?.(payload ?? {}));
    await Promise.resolve(onWeeklyRefresh?.());

    toast('success', 'Yoklama başarıyla kaydedildi.');
    onClose?.();
  };

  // api bağlayıcı
  const renewApi = useMemo(() => {
    return {
      listMemberPlans: async (memberId) => {
        const res = await membersApi.memberPackages(memberId);
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.items)) return res.items;
        if (Array.isArray(res?.content)) return res.content;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },

      // ✅ yeni endpoint varsayımı
      renewFromPlan: async (memberId, fromPlanId, startAt) => {
        if (!memberId) throw new Error('memberId boş');
        if (!fromPlanId) throw new Error('fromPlanId boş');
        if (!startAt) throw new Error('startAt boş');

        return membersApi.renewPackage(memberId, {
          memberId,
          fromMemberPlanId: fromPlanId,
          startAt,
        });
      },

      createCustomPlanForMember: async (memberId, payload) => {
        if (!memberId) throw new Error('memberId boş');

        const planSnapshot = payload?.planSnapshot ?? payload;
        if (!planSnapshot) throw new Error('planSnapshot alınamadı');

        return membersApi.createMemberPackage(memberId, {
          memberId,
          startAt: payload?.startAt ?? null,
          durationMonths: payload?.durationMonths ?? 1,
          planSnapshot,
          planPriceTry: payload?.planPriceTry ?? null,
          agreedPriceTry: payload?.agreedPriceTry ?? null,
          paidTry: payload?.paidTry ?? null,
          note: payload?.note ?? null,
        });
      },
      // ✅ RenewPackageModal PropTypes bunu istiyor
      createMemberPackage: async (memberId, payload) => {
        // aynı fonksiyona yönlendir
        return membersApi.createMemberPackage(memberId, payload);
      },
    };
  }, []);

  // Decision action handler
  const handleDecisionAction = async (action, pd) => {
    const pid = String(pd?.sessionParticipantId);
    if (!pid) return;

    if (action.code === 'USE_SAME_PLAN') {
      // ✅ Backend DTO: limitDecision / packageExceedPolicy alanları farklı
      // Senin AttendanceMarkRequest’te:
      // - limitDecision: USE_SAME_PLAN | REQUIRE_SELECTED_PLAN
      // - packageExceedPolicy: KEEP_CURRENT_PLAN | SWITCH_TO_SELECTED_AND_END_CURRENT
      // Bu yüzden burada "limitDecision" set’leyeceğiz.
      setByPerson((prev) => ({
        ...prev,
        [pid]: {
          ...prev[pid],
          limitDecision: 'USE_SAME_PLAN',
          // packageExceedPolicy burada gerekmez; switch değil
          packageExceedPolicy: 'KEEP_CURRENT_PLAN',
        },
      }));

      setDecisionModalOpen(false);

      // ✅ tekrar validate edip aynı modalı tekrar açtırma: direkt commit
      Promise.resolve().then(() =>
        confirmDecisionAndCommit({ onSave: handleSaved, onClose, forceCommit: true })
      );
      return;
    }

    if (action.code === 'OPEN_PLAN_CREATE') {
      setDecisionModalOpen(false);
      setRenewParticipant(pd);
      setRenewOpen(true);
      return;
    }
  };
  const isPendingRow = (p) => {
    const st = String(p?.current?.status ?? p?.attendanceStatus ?? 'PENDING').toUpperCase();
    return st === 'PENDING';
  };
  const toast = (type, message) => {
    uiBus.emit('toast', { type, message });
  };

  const errMsg = (e, fallback) => {
    // axios style
    return (
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      fallback ||
      'Bir hata oluştu.'
    );
  };

  return (
    <>
      <AttendanceDecisionModal
        open={decisionModalOpen}
        decision={decision}
        onClose={() => setDecisionModalOpen(false)}
        onConfirm={async () => {
          await confirmDecisionAndCommit({
            onSave: handleSaved,
            onClose,
          });
          setDecisionModalOpen(false); // sadece decision modal kapansın
        }}
        onAction={handleDecisionAction}
      />

      <RenewPackageModal
        open={renewOpen}
        onClose={() => {
          setRenewOpen(false);
          setRenewParticipant(null);
        }}
        member={{ id: renewParticipant?.memberId }}
        participant={renewParticipant}
        api={renewApi}
        startAt={session?.dayISO}
        activities={[]}
        onDone={(res) => {
          const memberPlanId = res?.memberPlanId ?? res?.newMemberPlanId ?? res?.id ?? null;

          const pid = String(renewParticipant?.sessionParticipantId);
          if (!pid) return;

          setByPerson((prev) => ({
            ...prev,
            [pid]: {
              ...prev[pid],
              selectedMemberPlanId: memberPlanId,
              // ✅ yeni paket seçildi => "selected zorunlu" çizgisine çek
              limitDecision: 'REQUIRE_SELECTED_PLAN',
              packageExceedPolicy: 'SWITCH_TO_SELECTED_AND_END_CURRENT',
            },
          }));

          Promise.resolve().then(() => validateAndMaybeCommit({ onSave: handleSaved, onClose }));
        }}
      />

      <Dialog
        open={!!open}
        onClose={onClose}
        fullWidth
        fullScreen={isMobile} // ✅ ekle
        maxWidth={isMobile ? false : 'sm'} // ✅ ekle
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '22px',
            overflow: 'hidden',
            border: isMobile ? 'none' : `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
            background: isDark ? alpha('#0b1220', 0.94) : alpha('#ffffff', 0.99),
            backgroundImage: 'none',
            boxShadow: isMobile
              ? 'none'
              : '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,.45)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <MDBox
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(theme.palette.info.main, 0.14),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                  flex: '0 0 auto',
                }}
              >
                <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>how_to_reg</Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
                    {pageMode === 'VIEW' ? 'Yoklama Detayı' : 'Yoklama Düzenle'}
                  </MDTypography>

                  <Chip
                    size="small"
                    label={pageMode === 'VIEW' ? 'Detay' : 'Düzenleme'}
                    sx={{
                      height: 22,
                      borderRadius: '999px',
                      fontWeight: 900,
                      bgcolor: alpha(
                        pageMode === 'VIEW' ? theme.palette.info.main : theme.palette.warning.main,
                        0.12
                      ),
                      border: `1px solid ${alpha(
                        pageMode === 'VIEW' ? theme.palette.info.main : theme.palette.warning.main,
                        0.22
                      )}`,
                    }}
                  />
                </MDBox>

                <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block', mt: 0.35 }}>
                  {session?.dayISO || '—'} • {minToHm(session?.startMin || 0)} • {trName}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              {pageMode === 'EDIT' && invalidCount > 0 ? (
                <Chip
                  size="small"
                  label={`${invalidCount} kişi için açıklama gerekli`}
                  sx={{
                    height: 24,
                    borderRadius: '999px',
                    fontWeight: 900,
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                  }}
                />
              ) : null}

              <IconButton
                onClick={onClose}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) },
                }}
              >
                <Icon sx={{ color: alpha(theme.palette.error.main, 0.9) }}>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.18) }} />

        <DialogContent sx={{ pt: 1.3, pb: 1.1, px: isMobile ? 1.2 : 2 }}>
          {submitError ? (
            <MDBox
              sx={{
                mb: 1.1,
                p: 1.1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                bgcolor: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: theme.palette.error.main }}
              >
                İşlem başarısız
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.4 }}>
                {errText(submitError)}
              </MDTypography>
            </MDBox>
          ) : null}

          <MDBox
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <MDTypography variant="button" fontWeight="bold">
              Katılımcılar
            </MDTypography>

            {loading ? (
              <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <CircularProgress size={18} />
                <MDTypography variant="caption" sx={{ opacity: 0.82, fontWeight: 700 }}>
                  Yükleniyor...
                </MDTypography>
              </MDBox>
            ) : (
              <Chip
                size="small"
                label={`${(people || []).length} kişi`}
                sx={{
                  height: 24,
                  borderRadius: '999px',
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                  fontWeight: 900,
                }}
              />
            )}
          </MDBox>

          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {(people || []).map((p) => {
              const pid = String(p?.sessionParticipantId ?? p?.id);
              const pending = isPendingRow(p);
              const forceInlineEdit = pageMode === 'EDIT' && pending; // ✅ tıklamasız edit

              const openRow = forceInlineEdit ? true : String(expandedPid) === pid;

              const value = byPerson?.[pid] || {
                status: p?.current?.status ?? p?.attendanceStatus ?? S.PENDING,
                note: p?.current?.note ?? '',
                selectedMemberPlanId: null,
                limitDecision: null,
                packageExceedPolicy: null,
              };
              const pWithSession = { ...p, sessionDayISO: session?.dayISO }; // ✅

              return (
                <ParticipantAttendanceRow
                  key={pid}
                  p={pWithSession}
                  value={value}
                  readOnly={pageMode === 'VIEW'} // ✅
                  open={openRow}
                  mode={forceInlineEdit ? 'EDIT' : openRow ? expandedMode : 'DETAIL'}
                  onToggleDetail={() => {
                    if (forceInlineEdit) return; // ✅ pending satırda toggle yok
                    openRow ? closeAccordion() : openDetail(pid);
                  }}
                  onOpenEdit={() => {
                    if (pageMode === 'VIEW') return;
                    if (forceInlineEdit) return; // ✅ zaten edit açık
                    openRow && expandedMode === 'EDIT' ? closeAccordion() : openEdit(pid);
                  }}
                  onChange={(v) => {
                    if (pageMode === 'VIEW') return;
                    setByPerson((prev) => ({ ...prev, [pid]: v }));
                  }}
                  onSave={(v) => commitSingle(p.sessionParticipantId, v)}
                />
              );
            })}
          </MDBox>
        </DialogContent>

        <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.18) }} />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={onClose}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontWeight: 900,
              height: 46,
              flex: 1,
            }}
          >
            Kapat
          </MDButton>

          {pageMode === 'VIEW' ? (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setPageMode('EDIT')}
              sx={{
                borderRadius: '16px',
                textTransform: 'none',
                fontWeight: 900,
                height: 46,
                flex: 1,
              }}
            >
              Düzenle
            </MDButton>
          ) : (
            <Tooltip title={!hasAnyChange ? 'Değişiklik yok' : ''} placement="top">
              <span style={{ flex: 1 }}>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={() =>
                    validateAndMaybeCommit({
                      onSave: handleSaved,
                      onClose, // ✅ artık kapanabilir
                    })
                  }
                  disabled={
                    invalidCount > 0 || saveLoading || loading || !session?.id || !hasAnyChange
                  }
                  sx={{
                    borderRadius: '16px',
                    textTransform: 'none',
                    fontWeight: 900,
                    height: 46,
                    width: '100%',
                  }}
                >
                  {saveLoading
                    ? pendingCommit
                      ? 'Kaydediliyor...'
                      : 'Kontrol ediliyor...'
                    : !hasAnyChange
                    ? 'Değişiklik yok'
                    : 'Kaydet'}
                </MDButton>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!submitBlock}
        tone="error"
        title="İşlem engellendi"
        subtitle="Çakışma"
        message={submitBlock?.message}
        confirmText="Tamam"
        hideCancel
        onConfirm={() => setSubmitBlock(null)}
      />

      <ConfirmDialog
        open={!!submitWarn}
        tone="warning"
        title="Uyarı"
        subtitle="Kontrol et"
        message={submitWarn?.message}
        confirmText="Tamam"
        hideCancel
        onConfirm={() => setSubmitWarn(null)}
      />
    </>
  );
}

AttendanceMarkModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  session: PropTypes.object,
  trainersById: PropTypes.object,
  fetchParticipants: PropTypes.func,
  onSave: PropTypes.func,
  onWeeklyRefresh: PropTypes.func, // ✅ EKLE
};
